// Loyalty bonus-point promotions — pure port of getTotalLoyaltyPoints / isPromotionCurrentlyActive
// (Android UtilsMethod.kt:2133-2243). Kept IO-free (only ./money) so it's unit-testable and reusable.
//
// A promotion contributes its bonus_points to the earn rate when it is enabled AND "now" falls inside
// its active window: an optional active_hours time window (overnight-aware), the start/end date range
// (repeat_weekly ignores the end date), and the active_days weekday set.

import { toNum } from "./money";

function parseTimeToSeconds(t) {
  const sp = String(t || "").trim().split(":");
  if (sp.length < 2) return null;
  const h = parseInt(sp[0], 10);
  const m = parseInt(sp[1], 10);
  const s = sp[2] != null ? parseInt(sp[2], 10) || 0 : 0;
  if (isNaN(h) || isNaN(m)) return null;
  return h * 3600 + m * 60 + s;
}

const DAY_MAP = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }; // JS Date.getDay(): 0=Sun

export function isPromotionActive(p, now = new Date()) {
  if (!p) return false;
  const parts = String(p.active_hours ?? p.activeHours ?? "").split("_");
  const startSec = parts[0] && parts[0].trim() ? parseTimeToSeconds(parts[0]) : null;
  const endSec = parts[1] && parts[1].trim() ? parseTimeToSeconds(parts[1]) : null;
  const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const hasWindow = startSec != null && endSec != null;
  const isOvernight = hasWindow && endSec < startSec;

  // The anchor day owns this moment (an overnight-tail morning belongs to yesterday).
  const anchor = new Date(now);
  if (isOvernight && nowSec <= endSec) anchor.setDate(anchor.getDate() - 1);

  if (hasWindow) {
    const inWindow = isOvernight ? nowSec >= startSec || nowSec <= endSec : nowSec >= startSec && nowSec <= endSec;
    if (!inWindow) return false;
  }

  const startDate = String(p.start_date ?? p.startDate ?? "");
  const endDate = String(p.end_date ?? p.endDate ?? "");
  if (!startDate) return false;
  const anchorYmd = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, "0")}-${String(anchor.getDate()).padStart(2, "0")}`;
  const repeatWeekly = String(p.repeat_weekly ?? p.repeatWeekly ?? "") === "1";
  if (repeatWeekly) {
    if (anchorYmd < startDate) return false; // yyyy-MM-dd compares lexicographically
  } else {
    if (!endDate) return false;
    if (anchorYmd < startDate || anchorYmd > endDate) return false;
  }

  const activeDays = String(p.active_days ?? p.activeDays ?? "").trim();
  if (!activeDays) return false;
  const allowed = activeDays.split(",").map((d) => DAY_MAP[d.trim().toLowerCase()]).filter((d) => d != null);
  return allowed.includes(anchor.getDay());
}

// Σ bonus_points over enabled, currently-active promotions (Android getTotalLoyaltyPoints).
export function loyaltyBonusPoints(promos, now = new Date()) {
  if (!Array.isArray(promos)) return 0;
  return promos.reduce((s, p) => {
    if (!p) return s;
    const enabled =
      p.enable_promotion === true || p.enable_promotion === "1" || p.enablePromotion === true || p.enablePromotion === "1";
    return enabled && isPromotionActive(p, now) ? s + toNum(p.bonus_points ?? p.bonusPoints) : s;
  }, 0);
}
